import React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, FormItem, FormControl } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfigCombobox } from "@/components/shared/ConfigCombobox";

interface RoutingSectionProps {
  componentIndex: number;
}

export default function RoutingSection({
  componentIndex,
}: RoutingSectionProps) {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `components.${componentIndex}.routings`,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          2. Công đoạn gia công (Routings)
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({
              processName: "In Offset 4 Màu",
              processType: "PRINTING",
              isFixedCost: false,
              batchCost: 500000,
              unitCost: 150,
              setupWasteQty: 100,
              runWastePct: 2,
            })
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Thêm Công Đoạn
        </Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Tên Công Đoạn</TableHead>
              <TableHead className="w-[100px] text-center">
                Phí Cố Định
              </TableHead>
              <TableHead className="w-[130px]">Phí Lô Min (đ)</TableHead>
              <TableHead className="w-[130px]">Đơn giá (đ/tờ)</TableHead>
              <TableHead className="w-[120px]">Bù hao Set-up</TableHead>
              <TableHead className="w-[120px]">Bù hao Chạy %</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  Chưa có công đoạn nào
                </TableCell>
              </TableRow>
            ) : (
              fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`components.${componentIndex}.routings.${index}.processName`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ConfigCombobox
                              categoryCode="process_types"
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Chọn công đoạn"
                              hideLabel
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <FormField
                      control={control}
                      name={`components.${componentIndex}.routings.${index}.isFixedCost`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`components.${componentIndex}.routings.${index}.batchCost`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`components.${componentIndex}.routings.${index}.unitCost`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`components.${componentIndex}.routings.${index}.setupWasteQty`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`components.${componentIndex}.routings.${index}.runWastePct`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseFloat(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
